import { useMemo, useState } from "react";
import { Check, Clipboard } from "lucide-react";
import {
  resolveAmazonReportText,
  type AmazonSupportReview,
} from "@/lib/amazonSupportMessage";

type ReportReview = AmazonSupportReview & {
  asin?: string;
  amazonReviewId?: string;
  reviewUrl?: string | null;
  rating?: number;
};

export function amazonReportText(review: ReportReview): string {
  return resolveAmazonReportText(review).text;
}

export function AmazonReportText({ review }: { review: ReportReview }) {
  const [copied, setCopied] = useState(false);
  const resolved = useMemo(() => resolveAmazonReportText(review), [review]);

  async function copy() {
    await navigator.clipboard.writeText(resolved.text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Message for Amazon Brand Registry Support
          </div>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
              resolved.source === "stored_draft"
                ? "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900"
                : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900"
            }`}
          >
            {resolved.source === "stored_draft"
              ? "Saved draft"
              : "Fallback draft"}
          </span>
        </div>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
        {resolved.text}
      </p>
      {resolved.source === "deterministic_fallback" && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          No saved draft was available, so this standard fallback was prepared
          from the review. Review it before submitting.
        </p>
      )}
    </div>
  );
}
